import { useState } from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import cn from "clsx";
import { CopyToClipboard } from "react-copy-to-clipboard";
import JDIPrice from "@/common-components-src/js/price/index";
import UKTruncatedTooltip from "@/common-components-src/components/UKTruncatedTooltip";
import UKToast from "@/common-components-src/components/UKToast/index";
import VectorDown from "@/app/csr-src/assets/icons/VectorDown.svg";
import styles from "./index.module.scss";
import { PriceInfoVo, PriceInfoItemDTOListVo, OrderStatusVo } from "@/app/csr-src/apis/order/order.interface";
// 埋点
import { sendClickTrack, EXPOSURE_RECORD } from "@/tracks/25344";

interface orderDetailAmountProps {
  orderInfo: {
    detailInfo:
      | {
          label: string;
          value: string;
          customerName?: string;
          mobile?: string;
          isExpand?: boolean;
          showCopyButton?: boolean;
          copyButtonTitle?: string;
        }[]
      | null;
    priceInfoDTO: PriceInfoVo;
    orderStatusDTO: OrderStatusVo;
    orderId: string;
  };
}

const orderDetailAmount: React.FC<orderDetailAmountProps> = observer(({ orderInfo }) => {
  const { t } = useTranslation();
  const { detailInfo, priceInfoDTO, orderStatusDTO, orderId } = orderInfo || {};
  const { price, unit, savePrice, message, subTitle } = priceInfoDTO || {};
  const { orderStatusId } = orderStatusDTO || {};
  const priceInfoItemList = priceInfoDTO?.priceInfoItemDTOList || [];
  const [isItemViisible, setIsItemViisible] = useState(false);

  const commonTrackParams = { orderId: String(orderId), orderStatus: orderStatusId as "1" | "3" | "8" | "6" | "5" };

  // 展开与收起功能
  const toggleExpand = () => {
    setIsItemViisible(!isItemViisible);
  };

  // 复制订单号
  const copyHandler = () => {
    // 埋点
    sendClickTrack.Orderdetail_DetailCopy(commonTrackParams);
    UKToast.show({
      content: t("order.orderDetail.common.copySuccessTips"), // 弹窗内容
      icon: "success", // 可选：success, fail, loading, warn, info，影响图标
      duration: 2000, // 不写的话默认3000
    });
  };

  // 非金额信息楼层
  const mainInfoRender = () => {
    const dom = (
      <>
        {detailInfo?.length
          ? detailInfo.map((item, index) => {
              return item?.customerName ? (
                <div className={styles.info_item_special} key={index}>
                  <span className={styles.info_item_label}>{item.label}</span>
                  <div className={styles.address_item}>
                    {item.isExpand ? (
                      <>
                        <div onClick={toggleExpand} className={styles.expand_wrap}>
                          <VectorDown
                            className={cn(styles.more_ico, {
                              [styles.retract_ico]: isItemViisible,
                            })}
                          />
                        </div>
                        {isItemViisible ? (
                          <>
                            <div className={styles.address_item_inner}>
                              <UKTruncatedTooltip
                                text={item?.customerName}
                                tooltipStyles={styles.address_nick_name}
                                tooltipProps={{
                                  placement: "top",
                                  arrow: true,
                                }}
                              />
                              <UKTruncatedTooltip
                                text={item?.mobile}
                                tooltipStyles={styles.address_postcode}
                                tooltipProps={{
                                  placement: "top",
                                  arrow: true,
                                }}
                              />
                            </div>
                            <UKTruncatedTooltip
                              text={item.value}
                              tooltipStyles={styles.address_detail}
                              tooltipProps={{
                                placement: "top",
                                arrow: true,
                              }}
                            />
                          </>
                        ) : null}
                      </>
                    ) : (
                      <>
                        <div className={styles.address_item_inner}>
                          <UKTruncatedTooltip
                            text={item?.customerName}
                            tooltipStyles={styles.address_nick_name}
                            tooltipProps={{
                              placement: "top",
                              arrow: true,
                            }}
                          />
                          <UKTruncatedTooltip
                            text={item?.mobile}
                            tooltipStyles={styles.address_postcode}
                            tooltipProps={{
                              placement: "top",
                              arrow: true,
                            }}
                          />
                        </div>
                        <UKTruncatedTooltip
                          text={item.value}
                          tooltipStyles={styles.address_detail}
                          tooltipProps={{
                            placement: "top",
                            arrow: true,
                          }}
                        />
                      </>
                    )}
                  </div>
                </div>
              ) : item.value ? (
                <div className={styles.info_item} key={index}>
                  <span className={styles.info_item_label}>{item.label}</span>
                  <span className={styles.info_item_value}>
                    {item.value}
                    {item.showCopyButton && (
                      <CopyToClipboard text={item.value} onCopy={() => copyHandler()}>
                        <span
                          className={styles.copy_btn}
                          exptag={"exp|" + EXPOSURE_RECORD.Orderdetail_DetailCopyExpo}
                          data-exptag-config={JSON.stringify({
                            useClick: false,
                            stay_duration: 0.3,
                            repeated: false,
                            area_rate: 0.5,
                          })}
                          data-exptag-json-param={JSON.stringify(commonTrackParams)}
                        >
                          {item.copyButtonTitle}
                        </span>
                      </CopyToClipboard>
                    )}
                  </span>
                </div>
              ) : null;
            })
          : null}
      </>
    );
    return dom;
  };

  // 订单金额明细渲染
  const amountItemRender = (item: PriceInfoItemDTOListVo) => {
    let itemDom = null;

    if (item.price) {
      // 特殊样式 价格展示free
      if (item.priceDisplayType === 3) {
        itemDom = (
          <div>
            {item.originalPrice && (
              <JDIPrice
                symbol={item.unit || ""}
                price={item.originalPrice || ""}
                uiType={2}
                containerStyle={{
                  color: "#888B94",
                  marginRight: "8px",
                }}
                lineThrough
              />
            )}
            <span className={styles.free_tip_style}>{item.price}</span>
          </div>
        );
      } else if (item.priceDisplayType === 2) {
        // 展示负号
        itemDom = (
          <>
            {item.zf}
            <JDIPrice
              symbol={item.unit || ""}
              price={item.price}
              uiType={2}
              containerStyle={{
                color: "#1A1A1A",
              }}
            />
          </>
        );
      } else {
        itemDom = (
          <JDIPrice
            symbol={item.unit || ""}
            price={item.price}
            uiType={2}
            containerStyle={{
              color: "#1A1A1A",
            }}
          />
        );
      }
    }
    return itemDom;
  };

  // 金额信息楼层
  const amountInfoRender = () => {
    const dom = (
      <>
        <div className={cn(styles.info_item, styles.info_item_large)}>
          <span className={styles.info_item_label}>
            {message} {subTitle}
          </span>
          <div className={styles.right_amt}>
            {/* 节省金额暂不展示 */}
            {/* {Number(savePrice) ? (
              <span className={styles.save_price}>
                {t("order.orderDetail.orderDetailAmount.saveText")}{" "}
                <JDIPrice
                  symbol={unit || ""}
                  price={savePrice || ""}
                  uiType={2}
                  containerStyle={{
                    fontSize: "14px",
                    color: "#CC0C1C",
                  }}
                />
              </span>
            ) : null} */}
            {price ? (
              <span className={cn(styles.price_font, styles.price_large)}>
                <JDIPrice
                  symbol={unit || ""}
                  price={price || ""}
                  uiType={2}
                  containerStyle={{
                    color: "#1A1A1A",
                  }}
                />
              </span>
            ) : null}
          </div>
        </div>
        {priceInfoItemList?.length
          ? priceInfoItemList.map((item, index) => {
              return item.type === 2 ? (
                <div className={styles.info_item_divide} key={index}></div>
              ) : (
                <div className={styles.info_item} key={index}>
                  {/* 拼接展示Subtotal(x items) */}
                  <div className={styles.info_item_label}>
                    {item.title} {item.subTitle}
                  </div>
                  <div className={styles.price_font}>{amountItemRender(item)}</div>
                </div>
              );
            })
          : null}
      </>
    );
    return dom;
  };
  return (
    <div className={styles.wrap}>
      <div className={styles.left_info}>{mainInfoRender()}</div>
      <div className={styles.right_info}>{amountInfoRender()}</div>
    </div>
  );
});

export default orderDetailAmount;
